/**
 * 文化配置 Store
 * 管理当前用户的文化配置（日期格式、数字格式、货币等）
 */

import { defineStore } from "pinia";
import { store } from "../index";
import type { CultureConfig } from "@/api/country";
import {
  parseCultureConfig,
  formatDate,
  formatTime,
  formatDateTime,
  formatNumber,
  formatCurrency,
  parseNumber,
  validateDate,
  createFormatters,
} from "@/utils/dynamicCultureHelper";
import { DEFAULT_TIMEZONE_ID } from "@/config/timezone";

interface CultureState {
  config: CultureConfig | null;
  language: string; // 语言代码，如 'zh-CN', 'en-US'
  timezone: string; // 时区代码，如 'Asia/Shanghai', 'America/New_York'
}

export const useCultureStore = defineStore("culture", {
  state: (): CultureState => {
    return {
      config: null,
      language: "zh-CN", // 默认语言
      timezone: DEFAULT_TIMEZONE_ID, // 默认时区
    };
  },

  getters: {
    /**
     * 获取当前文化配置
     */
    getConfig(): CultureConfig | null {
      return this.config;
    },

    /**
     * 获取当前语言
     */
    getLanguage(): string {
      return this.language;
    },

    /**
     * 获取当前时区
     */
    getTimezone(): string {
      return this.timezone;
    },

    /**
     * 获取格式化器
     */
    getFormatters() {
      return createFormatters(this.config);
    },

    /**
     * 格式化日期（自动使用时区）
     */
    formatDate: (state) => (date: Date | string) => {
      if (!state.config) {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleDateString();
      }
      return formatDate(date, state.config, state.timezone);
    },

    /**
     * 格式化时间（自动使用时区）
     */
    formatTime: (state) => (date: Date | string) => {
      if (!state.config) {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleTimeString();
      }
      return formatTime(date, state.config, state.timezone);
    },

    /**
     * 格式化日期时间（自动使用时区）
     */
    formatDateTime: (state) => (date: Date | string) => {
      if (!state.config) {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleString();
      }
      return formatDateTime(date, state.config, state.timezone);
    },

    /**
     * 格式化数字
     */
    formatNumber: (state) => (value: number) => {
      if (!state.config) {
        return value.toLocaleString();
      }
      return formatNumber(value, state.config);
    },

    /**
     * 格式化货币
     */
    formatCurrency: (state) => (value: number) => {
      if (!state.config) {
        return `$${value.toLocaleString()}`;
      }
      return formatCurrency(value, state.config);
    },

    /**
     * 解析数字
     */
    parseNumber: (state) => (value: string) => {
      if (!state.config) {
        return parseFloat(value) || null;
      }
      return parseNumber(value, state.config);
    },

    /**
     * 验证日期
     */
    validateDate: (state) => (dateString: string) => {
      if (!state.config) {
        return true;
      }
      return validateDate(dateString, state.config);
    },
  },

  actions: {
    /**
     * 设置文化配置
     */
    setConfig(config: CultureConfig | string | null | undefined) {
      this.config = parseCultureConfig(config);
    },

    /**
     * 设置语言
     */
    setLanguage(language: string) {
      this.language = language;
    },

    /**
     * 设置时区
     */
    setTimezone(timezone: string) {
      this.timezone = timezone;
    },

    /**
     * 初始化文化配置（从国家配置初始化）
     */
    initFromCountry(country: {
      culture_config?: CultureConfig | string;
      language?: string;
      timezone?: string;
    }) {
      if (country.culture_config) {
        this.setConfig(country.culture_config);
      }

      if (country.language) {
        this.setLanguage(country.language);
      }

      if (country.timezone) {
        this.setTimezone(country.timezone);
      }
    },

    /**
     * 重置为默认值
     */
    reset() {
      this.config = null;
      this.language = "zh-CN";
      this.timezone = DEFAULT_TIMEZONE_ID;
    },
  },

  persist: {
    key: "culture-store",
    storage: sessionStorage,
    paths: ["language", "timezone"], // 只持久化语言和时区，配置从后端获取
  },
});

export const useCultureStoreWithOut = () => {
  return useCultureStore(store);
};
